import { Outlet } from "react-router-dom";
import { Box } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useContext, useEffect, useState } from "react";

import { AppErrorBoundary } from "../app_error_boundary";
import { Chat } from "../chat";
import { ChatErrorBoundary } from "../chat_error_boundary";
import { LeftMenu } from "../left_menu";
import { RightPanel } from "../right_panel";
import { SIDE_NAV_WIDTH } from "./const";
import { TopHeader } from "../top_header";
import { UnrecognizedCustomer } from "../unrecognized_customer";
import { LayoutContext } from "../../contexts/layout/context";
import { ViewTitleProvider } from "../../contexts/view_title/provider";
import { ViewActionsProvider } from "../../contexts/view_actions/provider";
import { IdTokenContext } from "../../contexts/authentication/amplify";
import { SysMessagesContext } from "../../contexts/sys_messages/context";
import { SysMessageLevel } from "../../contexts/sys_messages/type";

export const LayoutComponent = (props: {
  customerId: string | null;
  loading: boolean;
}) => {
  const { customerId, loading } = props;
  const {
    rightPanelWidth,
    isSidebarOpen,
    isNucleusExpanded = false,
  } = useContext(LayoutContext);
  const compactNucleus = useMediaQuery("(max-width:900px)");
  const expandedNucleusWorkspace = isNucleusExpanded && !compactNucleus;
  const { idToken } = useContext(IdTokenContext);
  const { addSystemMessage } = useContext(SysMessagesContext);

  // Track if we've ever had a successful customerId to avoid showing unrecognized page again
  const [hasEverHadCustomer, setHasEverHadCustomer] = useState(false);
  // Track if we've ever had a valid ID token to know when to show offline message
  const [hasEverHadToken, setHasEverHadToken] = useState(false);
  const [offlineMessageRemover, setOfflineMessageRemover] = useState<
    (() => void) | null
  >(null);
  // Track if we're waiting for customer ID resolution after token becomes available
  const [isResolvingCustomer, setIsResolvingCustomer] = useState(false);
  const [resolveTimeout, setResolveTimeout] = useState<NodeJS.Timeout | null>(
    null,
  );

  useEffect(() => {
    if (customerId) {
      setHasEverHadCustomer(true);
      setIsResolvingCustomer(false);
      // Clear any pending timeout
      if (resolveTimeout) {
        clearTimeout(resolveTimeout);
        setResolveTimeout(null);
      }
    }
  }, [customerId, resolveTimeout]);

  // Track when we've had a valid token
  useEffect(() => {
    if (idToken) {
      setHasEverHadToken(true);
      // When token becomes available but we don't have customerId yet, we're resolving
      if (!customerId && !hasEverHadCustomer) {
        setIsResolvingCustomer(true);
        // Set a timeout to stop resolving after 5 seconds to avoid infinite loading
        const timeout = setTimeout(() => {
          setIsResolvingCustomer(false);
          setResolveTimeout(null);
        }, 5000);
        setResolveTimeout(timeout);
      }
    }
  }, [idToken, customerId, hasEverHadCustomer]);

  // Handle offline/online system messages based on token expiry
  // Only show offline message if token exists but is expired, and we've had a token before
  useEffect(() => {
    const isExpired = (() => {
      if (!idToken) return false;
      try {
        const payload = JSON.parse(atob(idToken.split(".")[1]));
        const expMs = (payload?.exp || 0) * 1000;
        return Date.now() >= expMs;
      } catch {
        return false;
      }
    })();

    if (hasEverHadToken && !loading && isExpired) {
      if (!offlineMessageRemover) {
        const remover = addSystemMessage({
          level: SysMessageLevel.NEUTRAL,
          message:
            "App is currently offline. Please wait while we reconnect...",
        });
        setOfflineMessageRemover(() => remover);
      }
    } else if (!isExpired && offlineMessageRemover) {
      offlineMessageRemover();
      setOfflineMessageRemover(null);
    }
  }, [
    idToken,
    loading,
    hasEverHadToken,
    addSystemMessage,
    offlineMessageRemover,
  ]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (resolveTimeout) {
        clearTimeout(resolveTimeout);
      }
    };
  }, [resolveTimeout]);

  // Only show UnrecognizedCustomer if:
  // 1. We have a valid ID token (user is authenticated)
  // 2. We don't have a customerId (token doesn't have required customer/role)
  // 3. We've never had a successful customerId before (not a transient refresh issue)
  // 4. We're not currently resolving the customer ID (avoid flash during sign-in)
  const shouldShowUnrecognized =
    idToken &&
    !customerId &&
    !loading &&
    !hasEverHadCustomer &&
    !isResolvingCustomer;

  return (
    <Box sx={{ overflow: "hidden", height: "100vh", width: "100%" }}>
      <Box sx={{ display: "flex", height: "100%" }}>
        <Box sx={{ width: `${SIDE_NAV_WIDTH}px` }}>
          <LeftMenu />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: expandedNucleusWorkspace ? 0 : 1,
            width: expandedNucleusWorkspace ? 0 : "auto",
            maxWidth: expandedNucleusWorkspace
              ? 0
              : isSidebarOpen && !compactNucleus
                ? `calc(100% - ${SIDE_NAV_WIDTH}px - ${rightPanelWidth}px)`
                : `calc(100% - ${SIDE_NAV_WIDTH}px)`,
            overflow: "hidden",
            transition:
              "width 0.3s cubic-bezier(0.4, 0, 0.2, 1), max-width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <ViewTitleProvider>
            <ViewActionsProvider>
              <TopHeader />
              <AppErrorBoundary>
                {shouldShowUnrecognized && <UnrecognizedCustomer />}
                {customerId && <Outlet />}
              </AppErrorBoundary>
            </ViewActionsProvider>
          </ViewTitleProvider>
        </Box>
        <RightPanel>
          {isSidebarOpen && (
            <ChatErrorBoundary>
              <Chat />
            </ChatErrorBoundary>
          )}
        </RightPanel>
      </Box>
    </Box>
  );
};
